import React from 'react';
import { 
  Box, 
  Button, 
  Grid, 
  TextField, 
  Typography,
  Fade,
  Zoom
} from '@material-ui/core';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { addDepense } from '../redux/actions/depenseActions';
import { 
  AttachMoney, 
  Description,
  Add as AddIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { toast } from 'react-toastify';

const useStyles = makeStyles((theme) => ({
  formContainer: {
    padding: theme.spacing(1),
  },
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 700,
    color: '#2c5530',
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  iconAdornment: {
    color: '#2c5530',
    marginRight: theme.spacing(1),
  },
  submitButton: {
    background: 'linear-gradient(135deg, #2c5530 0%, #4a7c59 100%)',
    color: 'white',
    fontWeight: 600,
    fontSize: '1rem',
    padding: '12px 40px',
    borderRadius: 8,
    boxShadow: '0 4px 15px rgba(44, 85, 48, 0.3)',
    textTransform: 'none',
    minWidth: 200,
    marginTop: theme.spacing(2),
    '&:hover': {
      background: '#2c5530',
      boxShadow: '0 6px 20px rgba(44, 85, 48, 0.4)',
    },
    '&:disabled': {
      background: '#ccc',
      boxShadow: 'none',
    },
  },
  amountField: {
    '& input': {
      textAlign: 'right',
      fontWeight: 600,
      fontSize: '1.1rem',
    },
  },
  commentField: {
    '& textarea': {
      minHeight: '80px',
      resize: 'vertical',
    },
  },
  dateInfo: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: 8,
    marginBottom: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
  },
  summaryCard: {
    backgroundColor: '#f8fff9',
    padding: theme.spacing(2),
    borderRadius: 8,
    border: '1px solid #e8f5e8',
  }
}));

const DepenseForm = () => {
  const classes = useStyles();
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const initialValues = {
    intitule: '',
    montant: '',
    commentaire: '',
  };

  const validationSchema = Yup.object().shape({
    intitule: Yup.string()
      .required("L'intitulé est obligatoire")
      .min(3, "L'intitulé doit contenir au moins 3 caractères"),
    montant: Yup.number()
      .required("Le montant est obligatoire")
      .positive("Le montant doit être positif")
      .min(1, "Le montant minimum est de 1"),
    commentaire: Yup.string()
      .required("Le motif est obligatoire")
      .min(5, "Le motif doit contenir au moins 10 caractères")
      .max(500, "Le motif ne peut pas dépasser 500 caractères"),
  });

  const getCurrentDate = () => {
    const today = new Date();
    return {
      formatted: today.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
      iso: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
    };
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const onSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      const currentDate = getCurrentDate();
      
      const depense = {
        intitule: values.intitule.trim(),
        montant: parseFloat(values.montant),
        commentaire: values.commentaire.trim(),
        dateOper: currentDate.iso,
        speudo: auth.nom,
      };

      await dispatch(addDepense(depense));
      
      resetForm();
      
    } catch (error) {
      console.error('Erreur ajout dépense:', error);
      toast.error("Erreur lors de l'ajout de la dépense");
    } finally {
      setSubmitting(false);
    }
  };

  const currentDate = getCurrentDate();

  return (
    <Box className={classes.formContainer}>
      {/* Information date */}
      <Fade in timeout={500}>
        <Box className={classes.dateInfo}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Date d'opération
          </Typography>
          <Typography variant="body1" fontWeight="600">
            {currentDate.formatted}
          </Typography>
        </Box>
      </Fade>

      <Formik 
        initialValues={initialValues} 
        validationSchema={validationSchema} 
        onSubmit={onSubmit}
      >
        {({ values, errors, touched, isSubmitting, setFieldValue }) => (
          <Form>
            <Grid container spacing={3}>
              
              {/* Intitulé */}
              <Grid item xs={12}>
                <Field name="intitule">
                  {({ field, form }) => (
                    <TextField
                      {...field}
                      label="Intitulé de la dépense"
                      variant="outlined"
                      fullWidth
                      size="small"
                      error={touched.intitule && !!errors.intitule}
                      helperText={touched.intitule && errors.intitule}
                      required
                      InputProps={{
                        startAdornment: <Description className={classes.iconAdornment} />
                      }}
                      placeholder="Ex: Achat fournitures bureau, Frais de transport..."
                    />
                  )}
                </Field>
              </Grid>

              {/* Montant */}
              <Grid item xs={12}>
                <Zoom in timeout={600}>
                  <Field name="montant">
                    {({ field, form }) => (
                      <TextField
                        {...field}
                        label="Montant"
                        variant="outlined"
                        fullWidth
                        size="small"
                        error={touched.montant && !!errors.montant}
                        helperText={touched.montant ? errors.montant : `Valeur: ${formatCurrency(values.montant)}`}
                        required
                        className={classes.amountField}
                        InputProps={{
                          startAdornment: <AttachMoney className={classes.iconAdornment} />,
                          inputProps: { 
                            min: 1,
                            step: 100
                          }
                        }}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFieldValue("montant", value === '' ? '' : Math.max(0, parseFloat(value) || 0));
                        }}
                      />
                    )}
                  </Field>
                </Zoom>
              </Grid>

              {/* Commentaire/Motif */}
              <Grid item xs={12}>
                <Fade in timeout={800}>
                  <Field name="commentaire">
                    {({ field, form }) => (
                      <TextField
                        {...field}
                        label="Motif et détails"
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={4}
                        size="small"
                        error={touched.commentaire && !!errors.commentaire}
                        helperText={
                          touched.commentaire 
                            ? errors.commentaire 
                            : `${values.commentaire.length}/500 caractères`
                        }
                        required
                        className={classes.commentField}
                        placeholder="Décrivez en détail le motif de cette dépense..."
                        inputProps={{
                          maxLength: 500
                        }}
                      />
                    )}
                  </Field>
                </Fade>
              </Grid>

              {/* Résumé visuel (conditionnel) */}
              {values.intitule && values.montant && (
                <Grid item xs={12}>
                  <Zoom in timeout={500}>
                    <Box className={classes.summaryCard}>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        Récapitulatif:
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        <strong>Intitulé:</strong> {values.intitule}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Montant:</strong> {formatCurrency(values.montant)}
                      </Typography>
                    </Box>
                  </Zoom>
                </Grid>
              )}

              {/* Bouton de soumission */}
              <Grid item xs={12}>
                <Box display="flex" justifyContent="center" mt={2}>
                  <Button 
                    type="submit" 
                    variant="contained" 
                    className={classes.submitButton}
                    disabled={isSubmitting}
                    size="large"
                    startIcon={<AddIcon />}
                  >
                    {isSubmitting ? 'Enregistrement...' : 'Enregistrer la dépense'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default DepenseForm;